/**
 *
 * @component 甘特图插件封装为React 组件
 *
 **/

import { memo, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { gantt, Gantt } from 'gantt_commercial';
import moment from 'moment';
import { message, Dropdown } from 'antd';
import { useLocation } from 'react-router-dom';
import { PLAN_STATUS } from '&/utils/baseMacro';
import { editPlan, planSort } from '@/api/personCenter/projectSpace1.4';
import { queryTimes } from '&/api/personCenter/projectSpace';
import 'gantt_commercial/codebase/dhtmlxgantt.css';
import './ProgressGantt.scss';
const { NOT_STARTED, UNCONFIRM, UNDERWAY, PENDING, PASSED, FAILED } =
    PLAN_STATUS;

const ProgressGantt = forwardRef((props, ref) => {
    useImperativeHandle(ref, () => {
        // 需要将暴露的接口返回出去
        return {};
    });

    const {
        tasks = [],
        columns = [],
        i18n = 'cn', // cn or 'en'
        config,
        onClickTask,
        onTaskSelected,
        unSelect,
        isPublish = false, //  true=已发布    false=未发布
        onChange,
        permission,
        currentRole,
        contextMenu,
        onRightClick,
        statusDict,
        maxDay,
        minDay,
    } = props;
    const ganttContainer = useRef();
    const isPub = useRef(false);
    const perm = useRef([]);
    const role = useRef();

    function tansformDate(date, type) {
        return moment(date || undefined)
            [type]('day')
            .toDate();
    }

    useEffect(() => {
        isPub.current = isPublish;
        if (!isPublish) return;
        gantt.addTaskLayer({
            renderer: {
                render(task) {
                    const START = tansformDate(task.timeAt, 'startOf'),
                        END = tansformDate(task.timeEnd, 'endOf');
                    const sizes = gantt.getTaskPosition(task, START, END);
                    const el = document.createElement('div');
                    el.className = 'baseline';
                    el.setAttribute(gantt.config.task_attribute, task.id);
                    el.style.left = sizes.left + 'px';
                    el.style.width = sizes.width + 'px';
                    el.style.top =
                        sizes.top + gantt.config.bar_height + 13 + 'px';
                    return el;
                },
            },
            filter(task) {
                return ![NOT_STARTED, UNCONFIRM].includes(task.status);
            },
        });
        gantt.render(); // template需要重走的时候 调用？官方react栗子  DidUpdate调用
    }, [isPublish]);

    useEffect(() => {
        perm.current = permission;
        role.current = currentRole;
    }, [permission]);

    const layout = {
        css: 'gantt_container',
        cols: [
            {
                width: 431,
                rows: [
                    {
                        view: 'grid',
                        scrollX: 'gridScroll',
                        scrollable: false,
                        scrollY: 'scrollVer',
                    },
                    {
                        view: 'scrollbar',
                        id: 'gridScroll',
                        group: 'horizontal',
                    },
                ],
            },
            { resizer: true, width: 1 },
            {
                rows: [
                    {
                        view: 'timeline',
                        scrollX: 'scrollHor',
                        scrollY: 'scrollVer',
                    },
                    {
                        view: 'scrollbar',
                        id: 'scrollHor',
                        group: 'horizontal',
                    },
                ],
            },
            { view: 'scrollbar', id: 'scrollVer' },
        ],
    };

    const DEFAULT_CONFIG = {
        columns,
        show_progress: false,
        min_grid_column_width: 1,
        autoscroll: true,
        duration_unit: 'day',
        duration_step: 1,
        fit_tasks: true,
        drag_move: true,
        drag_resize: true,
        drag_links: false,
        order_branch: 'marker',
        order_branch_free: false,
        task_date: '%Y-%m-%d',
        reorder_grid_columns: true,
        min_column_width: 30,
        details_on_dblclick: false, // 双击打开插件自带编辑
        drag_progress: false,
        correct_work_time: true, // 允许将任务的开始和结束日期调整为工作时间(在拖动时)
        scales: [
            { unit: 'month', step: 1, format: '%Y - %m' }, // %M %F %m %n
            {
                unit: 'day',
                step: 1,
                format: '%d', // %d %l %j %D %y %m | format: function (date)
                css(date) {
                    let res = '';
                    if ([0, 6].includes(date.getDay())) {
                        res += 'weekend ';
                    }

                    if (moment().isSame(moment(date), 'day')) {
                        res += 'today';
                    }
                    return res;
                },
            },
        ],
        external_render: {
            isElement: React.isValidElement,
            renderElement: ReactDOM.render,
        },
        select_task: true,
        bar_height: 20,
        scale_height: 41,
        row_height: 41,
        autofit: false,
        layout,
        keep_grid_width: true,
        container_resize_method: 'timeout',
    };

    const DEFAULT_TEMPLATES = {
        // task_class 各种状态任务条的CSS
        task_class(start, end, task) {
            if (task.isDelay === '1' && task.status !== NOT_STARTED) {
                let res = 'type--danger type--hover';
                if (isPub.current || (role.current !== 8 && task.execute !== 1))
                    res += ' no--hover';
                return res;
            }

            switch (task.status) {
                case NOT_STARTED:
                case UNCONFIRM:
                    let res = 'type--unstart type--hover';
                    if (
                        isPub.current ||
                        (role.current !== 8 && task.execute !== 1)
                    )
                        res += ' no--hover';
                    return res;
                case UNDERWAY:
                    return 'type--underway';
                case PASSED:
                    return 'type--finished';
                default:
                    return 'type--danger';
            }
        },
        // 指定在树列中子项的标签之前插入的自定义内容 (图标前)
        grid_blank() {
            return '';
        },
        timeline_cell_class(task, date) {
            if ([0, 6].includes(date.getDay())) return 'weekend-grid';
        },
        //指定树列中子项的图标
        grid_file() {
            return '';
        },
        // 指定树列中父项的图标
        grid_folder() {
            return '';
        },
        //  任务条里的文本
        task_text(start, end, task) {
            return task.name;
        },
    };

    const selectId = useRef();
    const location = useLocation();
    const SEARCH = new URLSearchParams(location.search);
    const projectId = SEARCH.get('id');
    const original = useRef();
    const originalIndex = useRef(); // 垂直拖动前的index

    const drogStartCb = id => {
        originalIndex.current = gantt.getTaskIndex(id);
        return perm.current.includes('movePlan');
    };

    const drogEndCb = (originPlanId, target, tindex) => {
        const tTask = gantt.getTaskByIndex(tindex);
        if (originPlanId === tTask.id) return;
        const params = {
            projectId,
            originPlanId,
            targetPlanId: tTask.id,
        };
        planSort(params).catch(() => {
            gantt.moveTask(originPlanId, originalIndex.current);
            gantt.refreshData(); // 貌似是立即 更新甘特图的数据？
        });
        return true;
    };

    const pos = useRef(null);

    const limitMsg = '工期不能少于1天';

    //  计算施工时长
    async function calcDays(momentStart, momentEnd, params) {
        let times;
        if (params.isHoliday < 1) {
            times = momentEnd.diff(momentStart, 'day');
        } else {
            try {
                const { data } = await queryTimes(params);
                times = data.times;
            } catch {
                return Promise.reject();
            }
        }

        if (times < 1) {
            message.warning(limitMsg);
            return Promise.reject();
        }

        return Promise.resolve(times);
    }

    const afterTaskDrag = async id => {
        const task = gantt.getTask(id);
        const [momentStart, momentEnd] = [
            moment(task.start_date),
            moment(task.end_date).add(2, 's'),
        ];
        let hideMsg;
        try {
            if (momentStart.isSame(momentEnd, 'day')) {
                message.warning(limitMsg);
                throw new Error();
            }

            const timeAt = momentStart.format('YYYY-MM-DD'),
                timeEnd = momentEnd
                    .clone()
                    .subtract(1, 'day') // 当天开始结束计做一天
                    .format('YYYY-MM-DD');

            await calcDays(momentStart, momentEnd, {
                timeAt,
                timeEnd,
                isHoliday: task.isHoliday,
            });

            hideMsg = message.loading(intl.get('base.uploading'), 0);
            const { msg } = await editPlan({
                projectId,
                planId: id,
                dop: 1,
                isHoliday: task.isHoliday,
                timeAt,
                timeEnd,
            });
            const { x, y } = gantt.getScrollState();
            pos.current = { x, y };
            onChange();
            message.success(msg);
        } catch (error) {
            task.start_date = original.current.start_date;
            task.end_date = original.current.end_date;
            gantt.refreshData(); // 貌似是立即 更新甘特图的数据？
        } finally {
            hideMsg?.();
            gantt.updateTask(id);
        }
    };

    const beforeTaskDrag = id => {
        const task = gantt.getTask(id);
        const isPass =
            task.status === NOT_STARTED &&
            !isPub.current &&
            (role.current === 8 || task.execute === 1);
        if (isPass) {
            original.current = { ...gantt.getTask(id) };
        }
        return isPass;
    };

    const taskSelectHandler = id => {
        selectId.current = id;
        onTaskSelected?.(id);
    };

    //  右键菜单
    const [visible, setvisible] = useState(false);
    const dtRef = useRef();
    const showContextMenu = (taskId, _, event) => {
        if (!taskId) return;
        try {
            var x =
                    event.clientX +
                    document.body.scrollLeft +
                    document.documentElement.scrollLeft,
                y =
                    event.clientY +
                    document.body.scrollTop +
                    document.documentElement.scrollTop;
            dtRef.current.style.cssText += `left:${x}px;top:${y}px;`;
            gantt.selectTask(taskId);
            onRightClick(gantt.getTask(taskId));
            setvisible(true);
        } catch {}

        return false;
    };

    // 拖拽横道图时放大时间范围
    const changeScale = (id, mode, task) => {
        let state = gantt.getState();
        let minDate = state.min_date,
            maxDate = state.max_date;

        let scaleStep =
            gantt.date.add(new Date(), state.scale_step, state.scale_unit) -
            new Date();

        let showDate,
            repaint = false;
        if (['resize', 'move'].includes(mode)) {
            if (Math.abs(task.start_date - minDate) < scaleStep) {
                showDate = task.start_date;
                repaint = true;
            } else if (Math.abs(task.end_date - maxDate) < scaleStep) {
                showDate = task.end_date;
                repaint = true;
            }
            if (repaint) {
                gantt.render();
                gantt.showDate(showDate);
            }
        }
    };

    const changeDateScale = () => {
        if (!maxDay) return;
        let { min_date, max_date, scale_unit } = gantt.getState();
        const min = new Date(minDay);
        const max = new Date(maxDay);
        if (max > max_date) {
            gantt.config.end_date = gantt.calculateEndDate(max, 1, scale_unit);
        }
        if (min < min_date) {
            gantt.config.start_date = gantt.calculateEndDate(
                min,
                -1,
                scale_unit
            );
        }
    };

    useEffect(() => {
        gantt.plugins({
            tooltip: true,
            marker: true,
            click_drag: true,
            auto_scheduling: true,
            drag_timeline: true,
        });
        const EVENTS = [
            gantt.attachEvent('onTaskSelected', taskSelectHandler),
            gantt.attachEvent('onTaskDblClick', onClickTask),
            gantt.attachEvent('onBeforeTaskDrag', beforeTaskDrag),
            gantt.attachEvent('onAfterTaskDrag', afterTaskDrag),
            gantt.attachEvent('onRowDragStart', drogStartCb),
            gantt.attachEvent('onBeforeRowDragEnd', drogEndCb),
            gantt.attachEvent('onTaskDrag', changeScale),
            gantt.attachEvent('onContextMenu', showContextMenu),
        ];
        gantt.i18n.setLocale(i18n);
        Object.assign(gantt.config, DEFAULT_CONFIG);
        Object.assign(gantt.templates, DEFAULT_TEMPLATES);
        gantt.init(ganttContainer.current);

        configTooltips();
        changeDateScale();

        return () => {
            gantt.ext.tooltips.tooltip.hide();
            while (EVENTS.length) gantt.detachEvent(EVENTS.pop());
            gantt.config.end_date = undefined;
            gantt.config.start_date = undefined;
            gantt.config.fit_tasks = true;
            gantt.render();
            gantt.clearAll();
            Gantt.getGanttInstance().destructor();
        };
    }, []);

    const statusList = status => {
        switch (status) {
            case UNDERWAY:
            case PENDING:
                return {
                    className: 'underway',
                    icon: 'clock',
                };
            case PASSED:
                return {
                    className: 'finished',
                    icon: 'check-circle',
                };
            case FAILED:
            case UNCONFIRM:
                return {
                    className: 'postpone',
                    icon: 'clock',
                };

            default:
                return {
                    className: 'unstart',
                    icon: 'minus-circle',
                };
        }
    };

    function configTooltips(params) {
        // 去掉默认tooltips
        gantt.ext.tooltips.detach(
            '[' + gantt.config.task_attribute + ']:not(.gantt_task_row)'
        );

        // 添加自定义tooltips
        gantt.ext.tooltips.tooltipFor({
            selector: `.gantt_task [${gantt.config.task_attribute}]`,
            html: event => {
                if (gantt.config.touch && !gantt.config.touch_tooltip) {
                    return;
                }
                const targetTaskId = gantt.locate(event);
                if (gantt.isTaskExists(targetTaskId)) {
                    const task = gantt.getTask(targetTaskId);

                    const tag2 = `<div class="status--postpone">
                     ${intl.get('projectSpace.postponed')}
                 </div>`;
                    const tag = ` <div
                     class='status--${statusList(task.status).className}'>
                     ${statusDict[task.status ?? NOT_STARTED]?.name}
                 </div>`;

                    const realEnd = !!task.finishEnd
                        ? `${moment(task.finishEnd).format('MM-DD')} (${
                              task.finishTimes
                          }天)`
                        : '-';
                    const realTimes = !task.finishAt
                        ? ''
                        : ` <div class='practical-times'>${intl.get(
                              'projectSpace.reality'
                          )}${moment(task.finishAt).format('MM-DD')} ${intl.get(
                              'base.to'
                          )} ${realEnd}</div>`;
                    return `<div>
                                 <div class='toltip-title'>
                                     ${task.name}
                                     ${
                                         task.isDelay === '1' &&
                                         UNCONFIRM !== task.status
                                             ? tag2
                                             : tag
                                     }
                                 </div>
                                 ${realTimes}
                                 <div class='plan-times'>${intl.get(
                                     'projectSpace.planLimitLabel'
                                 )}${task.dateRange} (${task.times}${intl.get(
                        'templateSettings.days'
                    )})</div>
                             </div>`;
                }
                return null;
            },
            global: false,
        });
    }

    useEffect(() => {
        unSelect && gantt.unselectTask();
    }, [unSelect]);

    // 数据更新
    useEffect(() => {
        gantt.clearAll();
        gantt.parse({ tasks });
        gantt.addMarker({
            start_date: new Date(),
            css: 'today',
        });
        changeDateScale();
        if (pos.current) {
            const { x, y } = pos.current;
            gantt.scrollTo(x, y);
            pos.current = null;
        }
    }, [tasks]);

    useEffect(() => {
        if (!config) return;
        gantt.config = { ...gantt.config, ...config };
        gantt.render();
    }, [config]);

    return (
        <>
            <div ref={ganttContainer} className="progress-gantt" />
            <Dropdown
                visible={visible}
                trigger={['hover']}
                onVisibleChange={setvisible}
                overlay={contextMenu}>
                <div className="right-click-point" ref={dtRef} />
            </Dropdown>
        </>
    );
});

export default memo(ProgressGantt);
